import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Check, ExternalLink, MailX, Sparkles, Workflow } from 'lucide-react';
import { Badge, IconChip } from '../design/ui';
import { getStatusLabel } from '../constants/ticketStatuses';
import { formatShortDateTime } from '../utils/dates';
import './TicketAgentNotices.css';

// KI-272 / KI-273 (Kevin 23/10): the ticket's status is the AI agent's own
// choice; when its actions wait for the team, it shows here as a proposal to
// apply or ignore (kit § « Propositions de l'IA »: what it saw, what it
// proposes, Appliquer / Ignorer). A carrier address that refused a message is
// signalled here so the team fixes the carrier page or the rule that built it.

// « 28 oct., 14:05 » — the shared short format (utils/dates: naive UTC, Paris time).
const formatDate = (value) => formatShortDateTime(value, undefined, { empty: '' });

const TicketAgentNotices = ({ ticket, onTicketChange, onOpenRun }) => {
    const { t } = useTranslation();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    if (!ticket) return null;
    const proposal = ticket.status_proposal && ticket.status_proposal.status ? ticket.status_proposal : null;
    const refusals = Array.isArray(ticket.carrier_address_refusals) ? ticket.carrier_address_refusals : [];
    if (!proposal && refusals.length === 0) return null;

    const heldText = {
        client_off: t('agentNotices.held.clientOff', "the B2B client's automation is off"),
        account_off: t('agentNotices.held.accountOff', "the account's automation is off"),
        team: t('agentNotices.held.team', 'a person handles this ticket'),
        pilot: t('agentNotices.held.pilot', 'the AI wake-up runs in pilot mode'),
        draft: t('agentNotices.held.draft', 'it goes with the AI draft waiting for validation'),
    };
    const originText = {
        carrier_page_address: t('agentNotices.origin.address', 'an address of the carrier page'),
        carrier_page_template: t('agentNotices.origin.template', 'the address template of the carrier page'),
        routing_rule: t('agentNotices.origin.rule', 'a routing rule of the carrier page'),
        contact_rules: t('agentNotices.origin.contactRules', 'the contact rules of the carrier page'),
        not_on_carrier_page: t('agentNotices.origin.unconfigured', 'an address not configured on the carrier page'),
    };

    const act = async (action) => {
        setBusy(true);
        setError('');
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.post(`/api/tickets/${ticket._id}/status-proposal/${action}`, {}, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (action === 'apply') {
                onTicketChange && onTicketChange({ status: response.data.status, status_proposal: null });
            } else {
                onTicketChange && onTicketChange({ status_proposal: null });
            }
        } catch (err) {
            setError(t('agentNotices.actionFailed', 'The action failed, please retry.'));
        } finally {
            setBusy(false);
        }
    };

    const proposedAt = proposal ? formatDate(proposal.at) : '';

    return (
        <>
            {proposal && (
                <section className="u-card agent-notice agent-notice--proposal" aria-labelledby="agent-notice-proposal-title">
                    <div className="u-card__header agent-notice__header">
                        <IconChip icon={Sparkles} />
                        <div className="u-card__heading">
                            <h2 id="agent-notice-proposal-title" className="u-card__title agent-notice__title">
                                {t('ticketWorkspaceUi.proposal.title', 'Passer le ticket en « {{status}} »', {
                                    status: getStatusLabel(t, proposal.status),
                                })}
                                <Badge tone="accent">{t('ticketWorkspaceUi.proposal.badge', 'Proposition')}</Badge>
                            </h2>
                            <p
                                className="u-card__desc agent-notice__text"
                                title={proposal.summary ? `${heldText[proposal.held] || heldText.draft} — ${proposal.summary}` : undefined}
                            >
                                {t('agentNotices.proposalHeld', 'Not applied: {{reason}}.', {
                                    reason: heldText[proposal.held] || heldText.draft,
                                })}
                                {proposal.summary ? ` ${proposal.summary}` : ''}
                            </p>
                        </div>
                    </div>
                    <p className="agent-notice__change">
                        <span className="agent-notice__from">{getStatusLabel(t, ticket.status)}</span>
                        <ArrowRight className="agent-notice__arrow" strokeWidth={2} aria-label={t('ticketWorkspaceUi.proposal.becomes', 'devient')} />
                        <strong className="agent-notice__to">{getStatusLabel(t, proposal.status)}</strong>
                        {proposedAt && (
                            <span className="agent-notice__when">
                                {t('ticketWorkspaceUi.proposal.at', 'proposé par l’IA le {{date}}', { date: proposedAt })}
                            </span>
                        )}
                    </p>
                    {error && <p className="agent-notice__error u-error-text" role="alert">{error}</p>}
                    <div className="u-card__footer agent-notice__actions">
                        {proposal.run_id && onOpenRun ? (
                            <button type="button" className="u-btn u-btn--ghost u-btn--sm agent-notice__run" onClick={() => onOpenRun(proposal.run_id)}>
                                <Workflow strokeWidth={1.8} aria-hidden="true" />
                                {t('ticketWorkspaceUi.proposal.seeRun', 'Voir le raisonnement')}
                            </button>
                        ) : <span className="u-card__footer-note" />}
                        <button type="button" className="u-btn u-btn--secondary u-btn--sm" disabled={busy} onClick={() => act('dismiss')}>
                            {t('agentNotices.ignore', 'Ignore')}
                        </button>
                        <button type="button" className="u-btn u-btn--primary u-btn--sm" disabled={busy} onClick={() => act('apply')}>
                            <Check strokeWidth={2} aria-hidden="true" />
                            {t('agentNotices.apply', 'Apply')}
                        </button>
                    </div>
                </section>
            )}
            {refusals.length > 0 && (
                <section className="u-card agent-notice agent-notice--refused" role="alert">
                    <div className="u-card__header agent-notice__header">
                        <IconChip icon={MailX} tone="warning" />
                        <div className="u-card__heading">
                            <h2 className="u-card__title agent-notice__title">
                                {t('agentNotices.refusedTitle', 'Carrier address refused — fix the carrier page or the rule')}
                                <Badge tone="warning">{refusals.length}</Badge>
                            </h2>
                        </div>
                    </div>
                    <ul className="u-list agent-notice__list">
                        {refusals.map((entry) => (
                            <li key={`${entry.address}-${entry.conversation_id || ''}`} className="u-row agent-notice__row">
                                <div className="u-row__main">
                                    <div className="u-row__title"><code className="u-code agent-notice__address">{entry.address}</code></div>
                                    <div className="u-row__meta u-row__meta--wrap">
                                        {t('agentNotices.refusedLine', '({{code}}, {{date}}) — from {{origin}}', {
                                            code: entry.code || t('agentNotices.noCode', 'no code'),
                                            date: formatDate(entry.at),
                                            origin: originText[entry.origin] || originText.not_on_carrier_page,
                                        })}
                                        {entry.origin_entry ? ` « ${entry.origin_entry} »` : ''}
                                    </div>
                                </div>
                                {entry.carrier_page_id && (
                                    <div className="u-row__side">
                                        <Link className="u-btn u-btn--secondary u-btn--sm" to={`/carriers-instructions/${entry.carrier_page_id}`}>
                                            {t('agentNotices.openCarrierPage', 'Open the {{carrier}} carrier page', {
                                                carrier: entry.carrier || '',
                                            })}
                                            <ExternalLink strokeWidth={1.8} aria-hidden="true" />
                                        </Link>
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                </section>
            )}
        </>
    );
};

export default TicketAgentNotices;
